"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Search,
  FileText,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Scale,
  Sparkles,
  Lock,
  Database,
  Award,
  HelpCircle,
  X,
  Building2,
  Cpu,
  Bot,
  ShieldAlert,
} from "lucide-react";
import { NAVY, NAVY_DEEP, BLUE, BLUE_DARK, CARD_BORDER, TEXT_DARK } from "@/lib/tokens";
import { TreatmentBadge } from "@/components/Badges";
import UserNavMenu from "@/components/UserNavMenu";
import { getCurrentUser } from "@/lib/api";

const TRUSTED_FIRMS = [
  "MacLean Litigation",
  "Harbourfront Law Group",
  "Delgado & Associates",
  "Meridian Barristers",
  "Kingsway LLP",
  "Osler & Partners QC",
];

const SHOWCASE_TABS = [
  {
    id: "search",
    title: "Semantic Precedent Search",
    desc: "Search 700,000+ Canadian court judgments by meaning, not just keyword.",
    icon: Search,
  },
  {
    id: "chatbot",
    title: "Legal Chatbot",
    desc: "Ask factual legal questions and receive answers grounded in Canadian case law.",
    icon: Bot,
  },
  {
    id: "drafting",
    title: "Document Generator",
    desc: "Draft formal court documents automatically with authority insertion.",
    icon: FileText,
  },
  {
    id: "auditor",
    title: "Risk Auditor",
    desc: "Scan legal pleadings, motion records, or contracts for limitation periods and evidentiary gaps.",
    icon: ShieldAlert,
  },

];

export default function HomePage() {
  const router = useRouter();

  // Active user session
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    getCurrentUser().then((user) => {
      if (user) setCurrentUser(user);
    });
  }, []);

  // Active showcase tab
  const [activeTab, setActiveTab] = useState("search");

  // Document Generator Showcase State (The 3 exact supported document types)
  const [selectedDocType, setSelectedDocType] = useState<"Smart Document Request List" | "Research Memo" | "Demand Letter">("Smart Document Request List");

  // Interactive Guided Trial Modal
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialEmail, setTrialEmail] = useState("");
  const [trialFirm, setTrialFirm] = useState("");
  const [trialSubmitted, setTrialSubmitted] = useState(false);

  const handleTrialSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTrialSubmitted(true);
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-800 font-sans selection:bg-blue-100 selection:text-blue-900">
      {/* 1. TOP NAVIGATION BAR */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100 transition-all">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          {/* Brand Logo */}
          <Link
            href="/"
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img
              src="/canlexa_maple_logo_v2.png"
              alt="CanLexa Logo"
              className="w-10 h-10 rounded-xl group-hover:opacity-90 transition-opacity object-contain"
            />
            <div className="flex flex-col">
              <span className="text-xl font-extrabold text-slate-900 tracking-tight flex items-center gap-1.5">
                CanLexa
                
              </span>
            </div>
          </Link>

          {/* Navigation Links (REMOVED LANDING PAGE LINK) */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <button
              onClick={() => scrollToSection("features")}
              className="hover:text-slate-900 transition-colors cursor-pointer"
            >
              AI Engine
            </button>
            <button
              onClick={() => scrollToSection("showcase")}
              className="hover:text-slate-900 transition-colors cursor-pointer"
            >
              Capabilities
            </button>
            <button
              onClick={() => scrollToSection("solutions")}
              className="hover:text-slate-900 transition-colors cursor-pointer"
            >
              Solutions
            </button>
            <button
              onClick={() => scrollToSection("security")}
              className="hover:text-slate-900 transition-colors cursor-pointer"
            >
              Security
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className="hover:text-slate-900 transition-colors cursor-pointer"
            >
              Pricing
            </button>
          </nav>

          {/* Action CTAs / User Profile Session */}
          <div className="flex items-center gap-3">
            {currentUser ? (
              <div className="flex items-center gap-3">
                <Link
                  href="/dashboard"
                  className="text-xs font-semibold text-white bg-[#0F172A] hover:bg-[#1E293B] px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <span>Go to Workspaces</span>
                  <ArrowRight size={14} />
                </Link>
                <UserNavMenu user={currentUser} />
              </div>
            ) : (
              <>
                <Link
                  href="/login"
                  className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-4 py-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer inline-flex items-center justify-center"
                >
                  Log In
                </Link>
                <button
                  onClick={() => {
                    setTrialSubmitted(false);
                    setTrialEmail("");
                    setTrialFirm("");
                    setShowTrialModal(true);
                  }}
                  className="text-sm font-semibold text-white px-5 py-2.5 rounded-lg shadow-sm hover:opacity-95 transition-all flex items-center gap-1.5 cursor-pointer"
                  style={{ background: "#0F172A" }}
                >
                  <span>Start Your Guided Trial</span>
                  <ArrowRight size={15} />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* 2. HERO SECTION */}
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-32 bg-gradient-to-b from-slate-50/70 via-white to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            {/* Top Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-50/80 border border-blue-200/80 text-blue-800 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-8 shadow-xs animate-in fade-in slide-in-from-bottom-3 duration-500">
              <Sparkles size={14} className="text-blue-600 shrink-0" />
              <span>
                Purpose-Built for Canadian Civil Litigation
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
              AI that elevates civil litigation{" "}
              <span className="text-blue-600">
                on every matter.
              </span>
            </h1>

            {/* Subtext */}
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed mb-10">
              Search 700,000+ Canadian court judgments by meaning, consult our legal chatbot, generate court pleadings, and audit document risk under Canadian law.
            </p>

            {/* Dual CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto mb-16">
              <button
                onClick={() => {
                  setTrialSubmitted(false);
                  setTrialEmail("");
                  setTrialFirm("");
                  setShowTrialModal(true);
                }}
                className="w-full sm:w-auto text-base font-semibold text-white px-8 py-4 rounded-xl shadow-lg shadow-slate-900/15 hover:bg-[#1E293B] transition-all flex items-center justify-center gap-2 cursor-pointer"
                style={{ background: "#0F172A" }}
              >
                <span>Start Your Guided Trial</span>
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => scrollToSection("features")}
                className="w-full sm:w-auto text-base font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 px-8 py-4 rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Explore AI Engine</span>
              </button>
            </div>

            {/* Interactive Preview Mockup Card */}
            <div className="w-full bg-white rounded-2xl border border-slate-200/80 shadow-2xl shadow-slate-300/40 p-5 sm:p-7 text-left overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  <span className="text-xs font-mono text-slate-400 ml-2">
                    canlexa.com / workspace / CV-25-00489012
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Canadian Legal AI Engine Active
                  </span>
                  <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-md">
                    ONSC Civil Litigation
                  </span>
                </div>
              </div>

              {/* Sample Floating Search Prompt inside Mockup */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-5 flex items-center gap-3">
                <Search className="h-5 w-5 text-blue-600 shrink-0" />
                <div className="text-sm font-medium text-slate-800 truncate">
                  &quot;Find wrongful dismissal precedents in ONSC where
                  reasonable notice exceeded 18 months for senior managers&quot;
                </div>
                <span className="ml-auto hidden sm:inline-block text-xs font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  412ms
                </span>
              </div>

              {/* Precedent Citation Cards inside Mockup */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-blue-700">
                      2019 ONSC 4471
                    </span>
                    <TreatmentBadge type="good_law" />
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-1">
                    Woodgreen Plaza Ltd. v. Tam
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Court weighed 20 years of managerial service and specialized
                    industry expertise; awarded 22 months common law reasonable
                    notice.
                  </p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-blue-700">
                      [1960] O.W.N. 253
                    </span>
                    <TreatmentBadge type="good_law" />
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-1">
                    Bardal v. Globe &amp; Mail Ltd.
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Foundational authority establishing character of employment,
                    length of service, age, and availability of similar employment
                    factors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. TRUSTED FIRMS BANNER */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-8">
            Trusted by leading litigation boutiques &amp; civil litigators
            across Canada
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14 opacity-70">
            {TRUSTED_FIRMS.map((firm) => (
              <div
                key={firm}
                className="flex items-center gap-2 text-sm sm:text-base font-bold text-slate-600 tracking-tight"
              >
                <Building2 size={18} className="text-slate-400" />
                <span>{firm}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. CORE VALUE PROPOSITION GRID (AI ENGINE) */}
      <section id="features" className="py-20 md:py-28 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3">
              CanLexa AI Engine
            </h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Capture and compound your firm&apos;s collective legal knowledge.
            </h3>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              From private infrastructure to advanced research memos, CanLexa
              connects every capability into one continuously learning
              litigation system.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Feature Card 1: Semantic Search */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                  <Search size={24} />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  Semantic Precedent Search
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed mb-4">
                  Search over 700,000 Canadian judgments by factual scenario and legal intent. Automatic treatment badges flag Good Law, Distinguished, or Caution.
                </p>
              </div>
              <div className="text-xs font-semibold text-blue-600 flex items-center gap-1">
                <span>Vector + Keyword Hybrid Engine</span>
                <ArrowRight size={13} />
              </div>
            </div>

            {/* Feature Card 2: Legal Chatbot */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-5">
                  <Bot size={24} />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  Legal Chatbot &amp; Assistant
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed mb-4">
                  Interact with a legal RAG chatbot to query precedents, analyze uploaded case documents, and receive answers grounded with verified citations.
                </p>
              </div>
              <div className="text-xs font-semibold text-indigo-600 flex items-center gap-1">
                <span>Conversational RAG Pipeline</span>
                <ArrowRight size={13} />
              </div>
            </div>

            {/* Feature Card 3: Document Generator */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
                  <FileText size={24} />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  Document Generator
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed mb-4">
                  Automatically generate formal legal documents (Smart Document Request List, Research Memo, Demand Letter) directly from case facts.
                </p>
              </div>
              <div className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
                <span>3 Supported Document Types</span>
                <ArrowRight size={13} />
              </div>
            </div>

            {/* Feature Card 4: Risk Auditor */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mb-5">
                  <ShieldAlert size={24} />
                </div>
                <h4 className="text-base font-bold text-slate-900 mb-2">
                  Risk Auditor
                </h4>
                <p className="text-xs text-slate-600 leading-relaxed mb-4">
                  Upload legal pleadings, motion records, or contracts to automatically detect limitation periods, evidentiary gaps, and contractual vulnerabilities.
                </p>
              </div>
              <div className="text-xs font-semibold text-rose-600 flex items-center gap-1">
                <span>Red Flag &amp; Legal Risk Audit</span>
                <ArrowRight size={13} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. INTERACTIVE 5-TAB SHOWCASE SECTION */}
      <section id="showcase" className="py-20 md:py-28 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3">
              Enterprise Architecture
            </h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              AI built for the enterprise of law.
            </h3>
            <p className="text-base sm:text-lg text-slate-600">
              Explore how CanLexa integrates research, document generation, risk auditing, and security into a single unified firm workspace.
            </p>
          </div>

          {/* Tab Selector Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10">
            {SHOWCASE_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                    isActive
                      ? "bg-[#0F172A] text-white shadow-md"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <Icon size={17} />
                  <span>{tab.title}</span>
                </button>
              );
            })}
          </div>

          {/* Interactive Showcase Container */}
          <div className="w-full bg-slate-900 rounded-2xl p-6 sm:p-10 text-white shadow-2xl border border-slate-800">
            {activeTab === "search" && (
              <div className="animate-in fade-in duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
                  <div>
                    <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
                      CAPABILITY #1
                    </span>
                    <h4 className="text-xl sm:text-2xl font-bold text-white mt-1">
                      Semantic Precedent Search with Judicial Treatment
                    </h4>
                  </div>
                  <span className="text-xs font-mono bg-slate-800 text-slate-300 px-3 py-1.5 rounded-lg border border-slate-700">
                    700,000+ Canadian Court Judgments Indexed
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-4">
                    <div className="bg-slate-800/80 rounded-xl p-5 border border-slate-700/80">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-blue-400">
                          2021 ONCA 210 • Court of Appeal
                        </span>
                        <TreatmentBadge type="good_law" />
                      </div>
                      <div className="text-base font-bold text-white mb-2">
                        Kingsway Holdings v. Patel Foods Inc.
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        &quot;Relief from forfeiture granted where arrears were cured promptly; court weighed conduct and disproportionate loss under section 20 of the Commercial Tenancies Act.&quot;
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-800/40 rounded-xl p-5 border border-slate-800 flex flex-col justify-between">
                    <div>
                      <h5 className="text-sm font-semibold text-slate-200 mb-3">
                        Why Civil Litigators Use Semantic Search
                      </h5>
                      <ul className="space-y-3 text-xs text-slate-300 leading-relaxed">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                          <span>Understands legal doctrines like reasonable notice, relief from forfeiture, and mitigation.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                          <span>Automatic precedent treatment parsing flags overturned or distinguished rulings.</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "chatbot" && (
              <div className="animate-in fade-in duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
                  <div>
                    <span className="text-xs font-semibold text-indigo-400 uppercase tracking-wider">
                      CAPABILITY #2
                    </span>
                    <h4 className="text-xl sm:text-2xl font-bold text-white mt-1">
                      Canadian Legal RAG Chatbot &amp; Case Assistant
                    </h4>
                  </div>
                  <span className="text-xs font-mono bg-slate-800 text-slate-300 px-3 py-1.5 rounded-lg border border-slate-700">
                    Live Statutory &amp; Precedent Grounding
                  </span>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700/80 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                      U
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-400 mb-1">User Question</div>
                      <p className="text-sm text-slate-200">
                        &quot;What is the limitation period for serving a third-party claim against a municipality under Canadian law?&quot;
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-800/80 rounded-xl p-4 border border-blue-500/30 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0 mt-0.5">
                      <Bot size={18} />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-indigo-400">CanLexa Legal Assistant</span>
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full font-mono">Verified Citations</span>
                      </div>
                      <p className="text-xs text-slate-200 leading-relaxed">
                        Under s. 44(10) of the Municipal Act, 2001, written notice must be served on the City Clerk within 10 days of occurrence. Under Rule 29.02, a Third Party Claim should be issued within 30 days after defending the main action.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Document Generator Showcase */}
            {activeTab === "drafting" && (
              <div className="animate-in fade-in duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
                  <div>
                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
                      CAPABILITY #3
                    </span>
                    <h4 className="text-xl sm:text-2xl font-bold text-white mt-1">
                      Automated Legal Document Generator
                    </h4>
                  </div>
                  <span className="text-xs font-mono bg-slate-800 text-slate-300 px-3 py-1.5 rounded-lg border border-slate-700">
                    3 Supported Document Types
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Selector Column */}
                  <div className="lg:col-span-4 space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                      Select Document Type
                    </div>
                    {(["Smart Document Request List", "Research Memo", "Demand Letter"] as const).map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedDocType(type)}
                        className={`w-full text-left text-xs p-3 rounded-xl border transition-all flex items-center justify-between cursor-pointer ${
                          selectedDocType === type
                            ? "bg-blue-600 border-blue-500 text-white font-bold shadow-sm"
                            : "bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-800"
                        }`}
                      >
                        <span>{type}</span>
                        {selectedDocType === type && <CheckCircle2 size={14} />}
                      </button>
                    ))}
                  </div>

                  {/* Document Render Output Column */}
                  <div className="lg:col-span-8 bg-slate-950 rounded-xl border border-slate-800 p-5 font-mono text-xs text-slate-300 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-800">
                        <span className="text-xs font-bold text-blue-400 flex items-center gap-1.5">
                          <FileText size={14} />
                          {selectedDocType}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-0.5 rounded">.TXT</span>
                          <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded font-bold">.PDF</span>
                        </div>
                      </div>

                      {selectedDocType === "Smart Document Request List" && (
                        <div className="space-y-2">
                          <div className="text-slate-400 font-bold">RE: DOCUMENT REQUEST LIST — CV-25-00489012</div>
                          <p className="text-slate-400">Pursuant to Rule 30 of the Rules of Civil Procedure, Defendant requests production of:</p>
                          <ol className="list-decimal pl-4 space-y-1 text-slate-200">
                            <li>Executed Master Services Agreement &amp; Schedule A</li>
                            <li>Written Notice of Default &amp; Termination Correspondence</li>
                            <li>Audited Financial Statements for FY 2024-2025</li>
                          </ol>
                        </div>
                      )}

                      {selectedDocType === "Research Memo" && (
                        <div className="space-y-2">
                          <div className="text-slate-400 font-bold">MEMORANDUM OF LAW (CANADIAN IRAC ENGINE)</div>
                          <p><strong className="text-blue-400">ISSUE:</strong> Enforceability of oral lease renewal terms against statutory notice.</p>
                          <p><strong className="text-blue-400">RULE:</strong> Promissory Estoppel threshold under <em>Hryniak v. Mauldin (2014 SCC 7)</em>.</p>
                        </div>
                      )}

                      {selectedDocType === "Demand Letter" && (
                        <div className="space-y-2">
                          <div className="text-slate-400 font-bold">FORMAL DEMAND FOR PAYMENT &amp; SETTLEMENT</div>
                          <p className="text-red-400 font-bold">WITHOUT PREJUDICE</p>
                          <p className="text-slate-200">We act for Plaintiff Acme Corp. Take notice that unless $185,000 CAD is remitted within 14 days, court proceedings will commence in ONSC.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "auditor" && (
              <div className="animate-in fade-in duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
                  <div>
                    <span className="text-xs font-semibold text-rose-400 uppercase tracking-wider">
                      CAPABILITY #4
                    </span>
                    <h4 className="text-xl sm:text-2xl font-bold text-white mt-1">
                      Risk Auditor &amp; Pleading Red-Flag Engine
                    </h4>
                  </div>
                </div>
                <div className="bg-slate-950 rounded-xl border border-slate-800 p-5 space-y-3">
                  <div className="bg-red-500/10 border border-red-500/40 p-3 rounded-lg text-red-200 text-xs">
                    <strong>Rule 21.01(1)(b) Failure:</strong> Allegation of fraud in Para 18 lacks factual particulars.
                  </div>
                </div>
              </div>
            )}


          </div>
        </div>
      </section>

      {/* 6. PRACTICE AREA SOLUTIONS SECTION */}
      <section id="solutions" className="py-20 md:py-28 bg-slate-50/50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3">
              Practice Area Solutions
            </h2>
            <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Tailored for Civil Litigation Specialists
            </h3>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Whether handling commercial disputes, wrongful dismissal claims, or real estate litigation, CanLexa provides specialized statutory context.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200/80">
              <h4 className="text-lg font-bold text-slate-900 mb-2">Commercial Litigation</h4>
              <p className="text-xs text-slate-600 leading-relaxed mb-4">
                Analyze breach of contract, shareholder disputes, and injunctions under Commercial Tenancies Act and Business Corporations Act.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200/80">
              <h4 className="text-lg font-bold text-slate-900 mb-2">Employment Law</h4>
              <p className="text-xs text-slate-600 leading-relaxed mb-4">
                Calculate common law reasonable notice periods using Bardal factors and evaluate ESA minimum statutory entitlements.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200/80">
              <h4 className="text-lg font-bold text-slate-900 mb-2">Real Estate Disputes</h4>
              <p className="text-xs text-slate-600 leading-relaxed mb-4">
                Audit real estate purchase agreements, certificates of pending litigation (CPL), and commercial lease forfeiture claims.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. SECURITY & COMPLIANCE SECTION */}
      <section id="security" className="py-20 md:py-28 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 block">
            Canadian Compliance &amp; Security
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
            Built for Canadian Legal Compliance Standards
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mx-auto mb-14">
            Strict compliance, zero model training on client documents, and local Toronto cloud residency.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200/80 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
                <Database size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Toronto Data Residency</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                All case data and client files remain strictly stored within Canadian data centers (Toronto, ON).
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200/80 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">PIPEDA Compliant</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Complies with Canadian federal and provincial privacy legislation. Zero training on firm documents.
              </p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200/80 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
                <Lock size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">AES-256 Encryption</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Bank-grade encryption at rest and in transit protecting attorney-client privileged communications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. PRICING SECTION */}
      <section id="pricing" className="py-20 md:py-28 bg-slate-50/50 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-3 block">
            Transparent Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
            Plans Designed for Civil Litigators
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mx-auto mb-12">
            Start with a guided trial sandbox. No credit card required.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 flex flex-col justify-between shadow-xs">
              <div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-1">Solo Practitioner</h3>
                <p className="text-xs text-slate-500 mb-6">For individual civil litigation counsel.</p>
                <div className="text-3xl font-extrabold text-slate-900 mb-6">
                  $99 <span className="text-xs font-normal text-slate-500">CAD / mo</span>
                </div>
                <ul className="space-y-3 text-xs text-slate-600 mb-8">
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Full Precedent Search</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> 50 Document Risk Audits/mo</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Legal Chatbot Access</li>
                </ul>
              </div>
              <button
                onClick={() => setShowTrialModal(true)}
                className="w-full py-3 rounded-full border border-slate-200 hover:bg-slate-50 font-semibold text-xs text-slate-900 transition-colors cursor-pointer text-center"
              >
                Start Guided Trial
              </button>
            </div>

            <div className="bg-white rounded-2xl border-2 border-blue-600 p-8 flex flex-col justify-between shadow-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider">
                Most Popular
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-1">Firm Practice</h3>
                <p className="text-xs text-slate-500 mb-6">For litigation boutiques &amp; firm teams.</p>
                <div className="text-3xl font-extrabold text-slate-900 mb-6">
                  $249 <span className="text-xs font-normal text-slate-500">CAD / user / mo</span>
                </div>
                <ul className="space-y-3 text-xs text-slate-600 mb-8">
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Unlimited Precedent Search &amp; Audits</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Full Legal Chatbot &amp; Risk Auditor</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Document Generator (All 3 Types)</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Multi-Counsel Shared Workspaces</li>
                </ul>
              </div>
              <button
                onClick={() => setShowTrialModal(true)}
                className="w-full py-3 rounded-full bg-[#0F172A] hover:bg-[#1E293B] font-semibold text-xs text-white transition-colors cursor-pointer text-center shadow-sm"
              >
                Start Guided Trial
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-8 flex flex-col justify-between shadow-xs">
              <div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-1">Enterprise Firm</h3>
                <p className="text-xs text-slate-500 mb-6">Custom packaging for large law firms.</p>
                <div className="text-2xl font-extrabold text-slate-900 mb-6">
                  Custom CAD
                </div>
                <ul className="space-y-3 text-xs text-slate-600 mb-8">
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Everything in Firm Practice</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Custom Model Fine-Tuning on Firm Precedents</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={15} className="text-blue-600" /> Dedicated Account Manager &amp; SLA</li>
                </ul>
              </div>
              <button
                onClick={() => setShowTrialModal(true)}
                className="w-full py-3 rounded-full border border-slate-200 hover:bg-slate-50 font-semibold text-xs text-slate-900 transition-colors cursor-pointer text-center"
              >
                Contact Enterprise Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FOOTER */}
      <footer className="bg-[#0F172A] text-white py-16 mt-auto">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 pb-12">
            <div className="md:col-span-2 space-y-4">
              <div className="flex items-center gap-2">
                <img
                  src="/canlexa_maple_logo_v2.png"
                  alt="CanLexa Logo"
                  className="w-8 h-8 rounded-lg object-contain"
                />
                <span className="text-lg font-bold text-white">CanLexa</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed max-w-sm">
                Canada&apos;s AI platform purpose-built for civil litigation research, document drafting, and risk auditing.
              </p>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">
                AI Engine
              </div>
              <ul className="space-y-2 text-xs text-slate-400">
                <li>
                  <button onClick={() => scrollToSection("features")} className="hover:text-white transition-colors">
                    Semantic Precedent Search
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection("showcase")} className="hover:text-white transition-colors">
                    Legal Chatbot
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection("showcase")} className="hover:text-white transition-colors">
                    Document Generator
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection("showcase")} className="hover:text-white transition-colors">
                    Risk Auditor
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">
                Solutions
              </div>
              <ul className="space-y-2 text-xs text-slate-400">
                <li>Civil Litigation (ONSC)</li>
                <li>Commercial Disputes</li>
                <li>Employment Law</li>
                <li>Real Estate Litigation</li>
              </ul>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white mb-3">
                Company &amp; Compliance
              </div>
              <ul className="space-y-2 text-xs text-slate-400">
                <li>Canadian Legal Compliance</li>
                <li>PIPEDA Data Privacy</li>
                <li>Canadian Cloud Residency (Toronto)</li>
                <li>Security Overview</li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
            <div>
              &copy; {new Date().getFullYear()} CanLexa Inc. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              <span>Privacy Policy</span>
              <span>Terms of Service</span>
              <span>Security</span>
            </div>
          </div>
        </div>
      </footer>

      {/* 10. GUIDED TRIAL MODAL */}
      {showTrialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-bold text-slate-900">
                  Start Your Guided Trial
                </h3>
              </div>
              <button
                onClick={() => setShowTrialModal(false)}
                className="text-slate-400 hover:text-slate-600 rounded-lg p-1 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {trialSubmitted ? (
              <div className="py-6 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 size={28} />
                </div>
                <div className="font-bold text-slate-900 text-lg">
                  Trial Request Received!
                </div>
                <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                  We have prepared your dedicated civil litigation sandbox for <strong>{trialEmail}</strong>.
                </p>
                <div className="flex items-center gap-3 mt-4 w-full">
                  <button
                    onClick={() => {
                      setShowTrialModal(false);
                      router.push("/login");
                    }}
                    className="w-full py-2.5 rounded-lg text-sm font-semibold bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors"
                  >
                    Continue to Login
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleTrialSubmit} className="space-y-4">
                <p className="text-xs text-slate-500 leading-relaxed">
                  Request a dedicated Canadian litigation AI workspace for your firm with 700,000+ Canadian court judgments indexed.
                </p>
                <div>
                  <label htmlFor="trial_email" className="block text-xs font-semibold text-slate-700 mb-1">
                    Firm Work Email
                  </label>
                  <input
                    id="trial_email"
                    type="email"
                    required
                    placeholder="you@lawfirm.com"
                    value={trialEmail}
                    onChange={(e) => setTrialEmail(e.target.value)}
                    className="h-10 block w-full px-3.5 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="trial_firm" className="block text-xs font-semibold text-slate-700 mb-1">
                    Firm / Boutique Name
                  </label>
                  <input
                    id="trial_firm"
                    type="text"
                    required
                    placeholder="MacLean Litigation LLP"
                    value={trialFirm}
                    onChange={(e) => setTrialFirm(e.target.value)}
                    className="h-10 block w-full px-3.5 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F172A] focus:border-transparent"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowTrialModal(false)}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors"
                  >
                    Request Trial Workspace
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
